<script setup lang="ts">
import { computed } from 'vue';

import {
  ItinerarySegmentPlaceholderType,
  ItinerarySegmentPlaceholder,
} from '@apps/main/new-desktop/modules/itineraryButton';
import type { AccommodationSelectorType } from '@apps/main/new-desktop/modules/accommodationSelector';
import { AccommodationSelector } from '@apps/main/new-desktop/modules/accommodationSelector';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import { useFlightsFiltersStore } from '@apps/main/new-desktop/core/stores/filters/flightsFiltersStore';
import { useFerryFiltersStore } from '@apps/main/new-desktop/core/stores/filters/ferryFiltersStore';

import { AppFlicker, AppSingleSelect, AppBasePlaceholder } from '@libs/ui';
import { TransportType } from '@libs/trip-planner-interfaces';

import ActivityCardHeader from '../acitivity/ActivityCardHeader.vue';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';
import ChangeProductLink from '../ChangeProductLink.vue';
import type { ItineraryBookableProductLoaderSegment } from '../../../models';
import {
  isItineraryBookableTransportLoaderSegment,
  ItinerarySegmentType,
  ProductCardTopBarType,
} from '../../../models';
import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../../stores';
import AccommodationSegmentHeader from '../accommodation/AccommodationSegmentHeader.vue';
import FlightSegmentHeader from '../flight/FlightSegmentHeader.vue';
import FerrySegmentHeader from '../ferry/FerrySegmentHeader.vue';

interface ItineraryProductLoaderSegmentProps {
  segment: ItineraryBookableProductLoaderSegment;
  shouldHideProductLink: boolean;
}
const props = defineProps<ItineraryProductLoaderSegmentProps>();

const tripOverviewPageStore = useTripOverviewPageStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const filtersStore = useFiltersStore();
const flightsFiltersStore = useFlightsFiltersStore();
const ferryFiltersStore = useFerryFiltersStore();
const browserStore = useBrowserStore();

const { t } = i18n.global;

//NOTE: duplicated
const currentSelectedOrderType = computed<AccommodationSelectorType>(() => {
  if (props.segment.barType === ProductCardTopBarType.Selected) {
    return 'popularAccommodation';
  }

  return props.segment.barType === ProductCardTopBarType.Suggestion
    ? 'popularAccommodation'
    : 'cheapestAccommodation';
});
function openHotelFilterPanel(source: 'from-plus' | 'from-more'): void {
  statsigEventsStore.logTripOverviewPreferencesModalOpened({ source });
  if (browserStore.isSmallerThan900px) {
    tripOverviewPanelsStore.openHotelFiltersPanel(props.segment.ingredientId);
  } else {
    tripOverviewModalsStore.openHotelFiltersModal(props.segment.ingredientId);
  }
}

function removeHotelFilter(type: HotelFilter, id?: string): void {
  statsigEventsStore.logTripOverviewFiltersRemoved();
  filtersStore.removeFilterFromTripOverview(
    type,
    props.segment.ingredientId,
    id
  );
}

function openFlightFilterPanel(): void {
  flightsFiltersStore.setAllowedFlightIngredientIds([
    props.segment.ingredientId,
  ]);
  tripOverviewModalsStore.openFlightsFiltersModal();
}

function removeFlightFilter(tagId: string): void {
  flightsFiltersStore.removeFilter(tagId, props.segment.ingredientId);
}

function openFerryFilterModal(): void {
  if (browserStore.isLargerThan899px) {
    tripOverviewModalsStore.openFerryFiltersModal(props.segment.ingredientId);
  } else {
    tripOverviewPanelsStore.openFerryFiltersPanel(props.segment.ingredientId);
  }
}

function removeFerryFilter(tagId: string): void {
  ferryFiltersStore.removeFilter(tagId, props.segment.ingredientId);
}
</script>

<template>
  <ItineraryTimeline :segment-type="props.segment.segmentType">
    <template #dropdown>
      <div v-if="!props.shouldHideProductLink">
        <ItineraryTransportSelector
          v-if="isItineraryBookableTransportLoaderSegment(segment)"
          :transport-type="
            segment.segmentType === ItinerarySegmentType.Flight
              ? TransportType.Flight
              : TransportType.Ferry
          "
          :disabled="false"
          @select-transport="
            tripOverviewPageStore.selectTransport(segment, $event)
          "
        />
        <AccommodationSelector
          v-else-if="segment.segmentType === ItinerarySegmentType.Accommodation"
          :selected-option="currentSelectedOrderType"
          :disabled="false"
          :is-there-selected-accommodation="
            props.segment.barType === ProductCardTopBarType.Selected
          "
        />
        <AppSingleSelect
          v-else-if="segment.segmentType === ItinerarySegmentType.Activity"
          type="primary"
          :selected="{
            id: 1,
            label: t('planner.activity'),
            icon: 'activity',
          }"
          left-side-icon="activity"
          :options="[]"
          disabled
        />
      </div>
      <div v-else>
        <ItinerarySegmentPlaceholder
          v-if="isItineraryBookableTransportLoaderSegment(segment)"
          :transport-type="
            segment.segmentType === ItinerarySegmentType.Flight
              ? ItinerarySegmentPlaceholderType.Flight
              : ItinerarySegmentPlaceholderType.Ferry
          "
        />

        <ItinerarySegmentPlaceholder
          v-else-if="segment.segmentType === ItinerarySegmentType.Accommodation"
          :transport-type="ItinerarySegmentPlaceholderType.Accommodation"
        />

        <ItinerarySegmentPlaceholder
          v-else-if="segment.segmentType === ItinerarySegmentType.Activity"
          :transport-type="ItinerarySegmentPlaceholderType.Activity"
        />
      </div>
    </template>
    <template #header>
      <AccommodationSegmentHeader
        v-if="props.segment.segmentType === ItinerarySegmentType.Accommodation"
        :segment="props.segment"
        :print-mode="false"
        :should-hide-product-link="shouldHideProductLink"
        @add-filter="openHotelFilterPanel('from-plus')"
        @remove-filter="removeHotelFilter($event.type, $event.id)"
        @show-more-filters="openHotelFilterPanel('from-more')"
      />
      <ActivityCardHeader
        v-if="
          props.segment.segmentType === ItinerarySegmentType.Activity &&
          !props.shouldHideProductLink
        "
        :print-mode="false"
        @remove="
          tripOverviewPageStore.deleteIngredientById(segment.ingredientId)
        "
      />
      <FlightSegmentHeader
        v-if="
          (props.shouldHideProductLink &&
            props.segment.segmentType === ItinerarySegmentType.Flight) ||
          (!browserStore.isSmallerThan1200px &&
            props.segment.segmentType === ItinerarySegmentType.Flight)
        "
        :should-hide-product-link="props.shouldHideProductLink"
        :segment="props.segment"
        :print-mode="false"
        @show-more-filters="openFlightFilterPanel"
        @remove-filter="removeFlightFilter"
      />
      <FerrySegmentHeader
        v-if="props.segment.segmentType === ItinerarySegmentType.Ferry"
        :segment="props.segment"
        :print-mode="false"
        :should-hide-product-link="props.shouldHideProductLink"
        @show-more-filters="openFerryFilterModal"
        @remove-filter="removeFerryFilter"
      />
    </template>

    <template #large-header>
      <FlightSegmentHeader
        v-if="
          !props.shouldHideProductLink &&
          browserStore.isSmallerThan1200px &&
          props.segment.segmentType === ItinerarySegmentType.Flight
        "
        :should-hide-product-link="props.shouldHideProductLink"
        :segment="props.segment"
        :print-mode="false"
        @show-more-filters="openFlightFilterPanel"
        @remove-filter="removeFlightFilter"
      />
    </template>

    <template #content>
      <div class="product-card-loader">
        <div
          :class="[
            'product-card-loader-content',
            props.segment.segmentType,
            { 'remove-product-links': props.shouldHideProductLink },
          ]"
        >
          <AppFlicker
            height="spacing-16"
            width="spacing-80"
            border-radius="border-radius-8"
          />
          <AppFlicker
            height="spacing-16"
            width="spacing-48"
            border-radius="border-radius-8"
          />
        </div>
        <ChangeProductLink
          v-if="
            props.segment.segmentType !== ItinerarySegmentType.Activity &&
            props.segment.segmentType !== ItinerarySegmentType.Accommodation &&
            !props.shouldHideProductLink
          "
          :link-context="{ segmentType: props.segment.segmentType }"
          class="change-product-link"
        />
        <ChangeProductLink
          v-if="
            props.segment.segmentType === ItinerarySegmentType.Accommodation &&
            !props.shouldHideProductLink
          "
          :link-context="{
            segmentType: props.segment.segmentType,
            ingredientIndex: props.segment.ingredientIndex,
          }"
          class="change-product-link"
        />
      </div>
      <AppBasePlaceholder
        v-if="props.segment.segmentType === ItinerarySegmentType.Activity"
        class="activity-upsell"
        :title="
          t('overview_placeholder.activity_upsell.title', [
            props.segment.destination,
          ])
        "
        :description="t('overview_placeholder.activity_upsell.description')"
        :is-small-device="browserStore.isSmallerThan900px"
        :icon-properties="{
          name: 'activityOutlined',
          color: 'color-brand-coral',
        }"
        :actions="[
          {
            textToDisplay: t('placeholder.add_another_activity'),
            eventType: '',
          },
        ]"
        is-loading
      />
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.product-card-loader {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);

  .product-card-loader-content {
    padding: var(--spacing-16);
    background: var(--color-white);
    border: 1px solid var(--color-neutral-30);
    border-radius: var(--border-radius-8);

    display: flex;
    flex-flow: column;
    gap: var(--spacing-8);

    &.flight {
      height: 148px;
    }

    &.ferry {
      height: 168px;
    }

    &.accommodation {
      height: 380px;
      @include for-less-or-equal-than-1199-px {
        margin: 0 var(--spacing-12);
      }
    }

    &.activity {
      height: 302px;
    }
    &.remove-product-links {
      height: 254px;
    }

    @include for-less-or-equal-than-599-px {
      &.flight {
        height: 235px;
      }

      &.ferry {
        height: 322px;
      }

      &.accommodation {
        height: 479px;
      }

      &.activity {
        height: 367px;
      }
    }
  }

  .change-product-link {
    align-self: flex-end;
  }
}
.activity-upsell {
  margin-top: var(--spacing-24);
}
</style>
